import React, { createContext, useContext, useState, useCallback } from 'react'
import { API_BASE_URL } from '../utils/api'

interface WidgetParameters {
  [key: string]: any
}

interface DashboardContextType {
  getWidgetParameter: (widgetId: string, key: string, defaultValue?: any) => any
  updateWidgetParameter: (widgetId: string, key: string, value: any) => Promise<void>
  getWidgetParameters: (widgetId: string) => WidgetParameters
  setWidgetParameters: (widgetId: string, parameters: WidgetParameters) => void
}

const DashboardContext = createContext<DashboardContextType | null>(null)

interface DashboardProviderProps {
  children: React.ReactNode
  initialWidgetParameters?: { [widgetId: string]: WidgetParameters }
}

export const DashboardProvider: React.FC<DashboardProviderProps> = ({ 
  children, 
  initialWidgetParameters = {} 
}) => {
  const [widgetParameters, setWidgetParametersState] = useState<{ [widgetId: string]: WidgetParameters }>(
    initialWidgetParameters
  )

  const getWidgetParameter = useCallback((widgetId: string, key: string, defaultValue: any = null) => {
    return widgetParameters[widgetId]?.[key] ?? defaultValue
  }, [widgetParameters])

  const getWidgetParameters = useCallback((widgetId: string): WidgetParameters => {
    return widgetParameters[widgetId] || {}
  }, [widgetParameters])

  const setWidgetParameters = useCallback((widgetId: string, parameters: WidgetParameters) => {
    setWidgetParametersState(prev => ({
      ...prev,
      [widgetId]: parameters
    }))
  }, [])

  const updateWidgetParameter = useCallback(async (widgetId: string, key: string, value: any) => {
    // Update local state immediately
    setWidgetParametersState(prev => ({
      ...prev,
      [widgetId]: {
        ...prev[widgetId],
        [key]: value
      }
    }))

    // Save to database
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        console.error('No auth token, cannot save widget parameter')
        return
      }

      const response = await fetch(`${API_BASE_URL}/api/dashboard-config/widget/${widgetId}?layout=default`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parameters: { [key]: value }
        }),
      })

      if (!response.ok) {
        console.error('Failed to save widget parameter to database')
      }
    } catch (error) {
      console.error('Error saving widget parameter:', error)
    }
  }, [])

  const contextValue: DashboardContextType = {
    getWidgetParameter,
    updateWidgetParameter,
    getWidgetParameters,
    setWidgetParameters
  }

  return (
    <DashboardContext.Provider value={contextValue}>
      {children}
    </DashboardContext.Provider>
  )
}

export const useDashboard = (): DashboardContextType => {
  const context = useContext(DashboardContext)
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider')
  }
  return context
}
